import { useEffect, useState } from 'react';
import { apiRequest } from '../utils/api';

interface SiteSettings {
  colors: {
    primary: string;
    secondary: string;
    background: string;
    text: string;
    textMuted: string;
    border: string;
    accent: string;
  };
  typography: {
    fontFamily: string;
    headingFontFamily: string;
    h1Size: string;
    h2Size: string;
    h3Size: string;
    h4Size: string;
    bodySize: string;
    smallSize: string;
    h1Weight: string;
    h2Weight: string;
    h3Weight: string;
    bodyWeight: string;
    lineHeight: string;
  };
  layout: {
    maxWidth: string;
    containerPadding: string;
  };
}

export function SiteStylesProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<SiteSettings | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  async function loadSettings() {
    try {
      const data = await apiRequest('/site-settings');
      setSettings(data);
      applyStyles(data);
    } catch (error) {
      console.error('Failed to load site settings:', error);
    }
  }

  function applyStyles(settings: SiteSettings) {
    const root = document.documentElement;

    // Apply colors
    root.style.setProperty('--site-color-primary', settings.colors.primary);
    root.style.setProperty('--site-color-secondary', settings.colors.secondary);
    root.style.setProperty('--site-color-background', settings.colors.background);
    root.style.setProperty('--site-color-text', settings.colors.text);
    root.style.setProperty('--site-color-text-muted', settings.colors.textMuted);
    root.style.setProperty('--site-color-border', settings.colors.border);
    root.style.setProperty('--site-color-accent', settings.colors.accent);

    // Apply typography
    root.style.setProperty('--site-font-family', settings.typography.fontFamily);
    root.style.setProperty('--site-heading-font-family', settings.typography.headingFontFamily);
    root.style.setProperty('--site-h1-size', settings.typography.h1Size);
    root.style.setProperty('--site-h2-size', settings.typography.h2Size);
    root.style.setProperty('--site-h3-size', settings.typography.h3Size);
    root.style.setProperty('--site-h4-size', settings.typography.h4Size);
    root.style.setProperty('--site-body-size', settings.typography.bodySize);
    root.style.setProperty('--site-small-size', settings.typography.smallSize);
    root.style.setProperty('--site-h1-weight', settings.typography.h1Weight);
    root.style.setProperty('--site-h2-weight', settings.typography.h2Weight);
    root.style.setProperty('--site-h3-weight', settings.typography.h3Weight);
    root.style.setProperty('--site-body-weight', settings.typography.bodyWeight);
    root.style.setProperty('--site-line-height', settings.typography.lineHeight);

    // Apply layout
    root.style.setProperty('--site-max-width', settings.layout.maxWidth);
    root.style.setProperty('--site-container-padding', settings.layout.containerPadding);
  }

  return <>{children}</>;
}
